// frontend/my-react-app/src/pages/MedicationsPage.jsx
// Created: 2026-03-09
// Updated: 2026-03-09 — replaced mock data with fetchMedications API call (Piece 1c)
// Updated: 2026-03-10 — destructure { medications } from fetchMedications (now returns { medications, userTimezone })
// Updated: 2026-03-10 — grouped sections (Scheduled/As Needed/Stopped), alpha sort, 4-row card layout
// Updated: 2026-03-10 — dashboard-style segments (outline, blue header) for Scheduled/Take as Needed
// Updated: 2026-03-10 — use MedicationComponent.css for cards (same styles as dashboard)
// Medications list page — shows all active meds for the selected pet, with add/edit entry points.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import Header from '../Header';
import { usePet } from '../contexts/PetContext';
import { fetchMedications } from '../services/api';
import '../MedicationComponent.css';
import '../TimeSlotContainer.css';
import './MedicationsPage.css';

const DAY_SHORT = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' };
const DAY_FULL  = { 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday', 7: 'Sunday' };

function formatTime(hhmm) {
  const [hourStr, minute] = hhmm.split(':');
  const hour = parseInt(hourStr, 10);
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minute} ${period}`;
}

/** Trims trailing zeros: 1 → "1", 1.5 → "1.5", 1.25 → "1.25", 1.255 → "1.26" */
function formatQuantity(qty) {
  const n = parseFloat(qty);
  return n % 1 === 0 ? String(n) : parseFloat(n.toFixed(2)).toString();
}

/** Row 1: "Atenolol" or "Atenolol (Pepper's heart pill)" when display_name differs */
function getRow1(med) {
  if (med.display_name && med.display_name !== med.name) {
    return `${med.name} (${med.display_name})`;
  }
  return med.name;
}

/** Row 2: qty + format */
function getDosageLine(med) {
  return `${formatQuantity(med.quantity_to_take)} ${med.medication_format}`;
}

/** Row 3: frequency/days first, then times */
function getScheduleLine(med) {
  if (med.frequency_pattern === 'as_needed') return 'As needed';

  const times = (med.scheduled_times || []).map(formatTime).join(', ');

  if (med.frequency_pattern === 'daily') {
    return times ? `Every day · ${times}` : 'Every day';
  }
  if (med.frequency_pattern === 'weekly') {
    const day = DAY_FULL[med.days_of_week?.[0]] || '';
    return times ? `Every ${day} · ${times}` : `Every ${day}`;
  }
  // specific days
  const days = (med.days_of_week || [])
    .slice()
    .sort((a, b) => a - b)
    .map(d => DAY_SHORT[d])
    .join(', ');
  return times ? `${days} · ${times}` : days;
}

function sortAlpha(meds) {
  return [...meds].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
}

export default function MedicationsPage() {
  const { selectedPet, isLoading: petsLoading } = usePet();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [medications, setMedications] = useState([]);
  const [isLoadingMeds, setIsLoadingMeds] = useState(false);
  const [medsError, setMedsError] = useState(null);

  const loadMedications = useCallback(async () => {
    if (!selectedPet) return;
    setIsLoadingMeds(true);
    setMedsError(null);
    try {
      const { medications: meds } = await fetchMedications(getToken, selectedPet.id);
      setMedications(meds);
    } catch (err) {
      setMedsError('Could not load medications. Please try again.');
    } finally {
      setIsLoadingMeds(false);
    }
  }, [selectedPet, getToken]);

  useEffect(() => {
    loadMedications();
  }, [loadMedications]);

  const renderContent = () => {
    if (petsLoading) {
      return (
        <div className="meds-page-loading">
          Loading…
        </div>
      );
    }

    if (!selectedPet) {
      return (
        <div className="meds-page-no-pet">
          <div className="meds-page-no-pet-icon">🐾</div>
          <h3>No pet selected</h3>
          <p>Add a pet first before managing medications.</p>
        </div>
      );
    }

    if (isLoadingMeds) {
      return (
        <div className="meds-page-loading">
          Loading medications…
        </div>
      );
    }

    if (medsError) {
      return (
        <div className="meds-page-error">
          <p>{medsError}</p>
          <button className="btn-retry" onClick={loadMedications}>Try again</button>
        </div>
      );
    }

    if (medications.length === 0) {
      return (
        <div className="meds-page-empty">
          <div className="meds-page-empty-icon">💊</div>
          <h3>No medications yet for {selectedPet.name}.</h3>
          <p>Add one to start tracking and receive daily WhatsApp reminders.</p>
          <button className="btn-add-med" onClick={() => navigate('/meds/new')}>
            + Add Medication
          </button>
        </div>
      );
    }

    const scheduled = sortAlpha(medications.filter(m => m.frequency_pattern !== 'as_needed'));
    const asNeeded  = sortAlpha(medications.filter(m => m.frequency_pattern === 'as_needed'));
    // stopped is always empty until Piece 4 (stop medication) is built — backend filters to status='active'
    const stopped = [];

    const renderCard = (med) => (
      <button
        key={med.id}
        type="button"
        className="medication-component meds-list-card"
        onClick={() => navigate(`/meds/${med.id}/edit`)}
      >
        <div className="medication-info">
          <h3 className="medication-name">{getRow1(med)}</h3>
          <p className="dosage-info">{getDosageLine(med)}</p>
          <span className="instructions">{getScheduleLine(med)}</span>
          {med.purpose && (
            <span className="instructions"> · {med.purpose}</span>
          )}
        </div>
        <span className="meds-list-arrow" aria-hidden="true">›</span>
      </button>
    );

    return (
      <>
        <div className="meds-page-header">
          <div className="meds-page-title-row">
            <h2 className="meds-page-title">{selectedPet.name}'s Medications</h2>
            <button className="btn-add-med" onClick={() => navigate('/meds/new')}>
              + Add Medication
            </button>
          </div>
        </div>

        {scheduled.length > 0 && (
          <div className="time-slot meds-list-slot">
            <div className="time-slot-header">
              <h2 className="time-slot-title">Scheduled</h2>
              <span className="medication-count">
                {scheduled.length} med{scheduled.length > 1 ? 's' : ''}
              </span>
            </div>
            <div className="medications-list meds-list-inner">
              {scheduled.map(renderCard)}
            </div>
          </div>
        )}

        {asNeeded.length > 0 && (
          <div className="time-slot meds-list-slot">
            <div className="time-slot-header">
              <h2 className="time-slot-title">Take as Needed</h2>
              <span className="medication-count">
                {asNeeded.length} med{asNeeded.length > 1 ? 's' : ''}
              </span>
            </div>
            <div className="medications-list meds-list-inner">
              {asNeeded.map(renderCard)}
            </div>
          </div>
        )}

        {stopped.length > 0 && (
          <div className="time-slot meds-list-slot">
            <div className="time-slot-header">
              <h2 className="time-slot-title">Stopped</h2>
              <span className="medication-count">
                {stopped.length} med{stopped.length > 1 ? 's' : ''}
              </span>
            </div>
            <div className="medications-list meds-list-inner">
              {stopped.map(renderCard)}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="medications-page">
      <Header />
      <div className="meds-page-content">
        <div className="meds-page-inner">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
